// ===================================================================
// KAIXO - Admin API
// ===================================================================
// Wrapper sobre los endpoints /api/admin/*. Gestiona el JWT de área
// (token corto, 30 min) que se requiere para acceder a endpoints
// protegidos por área. El token vive en sessionStorage y se manda
// en header X-Admin-Area en cada request al área correspondiente.
// ===================================================================

import api from './api';

const AREA_TOKEN_KEY_PREFIX = 'kaixo_area_token_';

// Devuelve el token guardado para un área, o null si expiró/no existe.
export function getAreaToken(area) {
    try {
        const raw = sessionStorage.getItem(AREA_TOKEN_KEY_PREFIX + area);
        if (!raw) return null;
        const { token, expiresAt } = JSON.parse(raw);
        if (Date.now() > expiresAt) {
            sessionStorage.removeItem(AREA_TOKEN_KEY_PREFIX + area);
            return null;
        }
        return token;
    } catch {
        return null;
    }
}

function saveAreaToken(area, token, expiresInSec) {
    const expiresAt = Date.now() + expiresInSec * 1000;
    sessionStorage.setItem(
        AREA_TOKEN_KEY_PREFIX + area,
        JSON.stringify({ token, expiresAt })
    );
}

export function clearAreaToken(area) {
    sessionStorage.removeItem(AREA_TOKEN_KEY_PREFIX + area);
}

export function clearAllAreaTokens() {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(AREA_TOKEN_KEY_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach(k => sessionStorage.removeItem(k));
}

// Cabeceras a añadir cuando un endpoint requiera área
export function areaHeaders(area) {
    const token = getAreaToken(area);
    return token ? { 'X-Admin-Area': token } : {};
}

// ── Endpoints ──────────────────────────────────────────────

// GET /api/admin/me → roles + áreas accesibles + isGeneral
export const adminMe = () => api.get('/admin/me');

// POST /api/admin/renew-session — reanuda sesión caducada por inactividad
export const adminRenewSession = () => api.post('/admin/renew-session');

// POST /api/admin/area/unlock { area, password }
export const adminUnlockArea = async (area, password) => {
    const { data } = await api.post('/admin/area/unlock', { area, password });
    if (data?.ok && data.token) {
        saveAreaToken(area, data.token, data.expiresIn || 1800);
    }
    return data;
};

// POST /api/admin/area/change-password
export const adminChangeAreaPassword = (area, currentPassword, newPassword) =>
    api.post('/admin/area/change-password', { area, currentPassword, newPassword });

// Solo admin_general
export const adminGrantRole = (userId, role, areaPasswords) =>
    api.post('/admin/grant-role', { userId, role, areaPasswords });

export const adminRevokeRole = (userId, role) =>
    api.post('/admin/revoke-role', { userId, role });

export const adminResetAreaPassword = (userId, area, newPassword) =>
    api.post('/admin/reset-area-password', { userId, area, newPassword });

export const adminListAdmins = () => api.get('/admin/admins');

// Destitución completa: quita todos los roles, isAdmin=false, limpia
// contraseñas de área. Bloqueado para el superadmin del producto.
export const adminRemoveAdmin = (userId) =>
    api.post('/admin/remove-admin', { userId });

export const adminAuditLog = (params) =>
    api.get('/admin/audit-log', { params });

// Estadísticas
export const adminStatsOverview = () => api.get('/admin/stats/overview');
export const adminStatsTimeseries = (days = 30) =>
    api.get('/admin/stats/timeseries', { params: { days } });

// Verificaciones (admin) — requieren area token
export const adminVerificationsList = (params = {}) =>
    api.get('/admin/verifications', {
        params,
        headers: areaHeaders('verification')
    });
export const adminVerificationsGet = (id) =>
    api.get(`/admin/verifications/${id}`, {
        headers: areaHeaders('verification')
    });
export const adminVerificationApprove = (id, notes) =>
    api.post(`/admin/verifications/${id}/approve`, { notes },
        { headers: areaHeaders('verification') });
export const adminVerificationReject = (id, reason, notes) =>
    api.post(`/admin/verifications/${id}/reject`, { reason, notes },
        { headers: areaHeaders('verification') });
export const adminVerificationRequestInfo = (id, message) =>
    api.post(`/admin/verifications/${id}/request-info`, { message },
        { headers: areaHeaders('verification') });

// Verificaciones (usuario)
export const verificationSubmit = (formData) =>
    api.post('/verifications/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
export const verificationMine = () => api.get('/verifications/mine');
// GET /api/verifications/eligibility?level=basic|creator|business&channelId=...
export const verificationEligibility = (level, channelId) =>
    api.get('/verifications/eligibility', { params: { level, channelId: channelId || undefined } });

// ─── Usuarios admin ────────────────────────────────────────
export const adminUsersList = (params = {}) => api.get('/admin/users', { params });
export const adminUsersGet = (id) => api.get(`/admin/users/${id}`);
// Datos de facturación combinados (User + Verification) para prerellenar
// el formulario de nueva factura.
export const adminUsersBillingData = (id) => api.get(`/admin/users/${id}/billing-data`);

// Resumen ejecutivo de gastos (combina Plataformas + Expenses manuales)
export const adminExpensesSummary = () => api.get('/admin/expenses/summary');

// ── Plataformas (gestión de infraestructura externa) ──────────────
export const adminPlatformsList = () => api.get('/admin/platforms');
export const adminPlatformCreate = (data) => api.post('/admin/platforms', data);
export const adminPlatformUpdate = (id, data) => api.put(`/admin/platforms/${id}`, data);
export const adminPlatformDelete = (id) => api.delete(`/admin/platforms/${id}`);
export const adminPlatformRefresh = (id) => api.post(`/admin/platforms/${id}/refresh`);
export const adminPlatformsRefreshAll = () => api.post('/admin/platforms/refresh-all');
export const adminUserBan = (id, reason) => api.post(`/admin/users/${id}/ban`, { reason });
export const adminUserUnban = (id) => api.post(`/admin/users/${id}/unban`);
export const adminUserSuspend = (id, days, reason) =>
    api.post(`/admin/users/${id}/suspend`, { days, reason });
export const adminUserUnsuspend = (id) => api.post(`/admin/users/${id}/unsuspend`);
export const adminUserNote = (id, note) => api.post(`/admin/users/${id}/note`, { note });

// Contactos en la agenda KAIXO de un usuario (diagnóstico/limpieza)
export const adminUserContactsList = (id) => api.get(`/admin/users/${id}/contacts`);
export const adminUserContactsCleanupOrphans = (id) => api.post(`/admin/users/${id}/contacts/cleanup-orphans`);
export const adminContactDelete = (contactId) => api.delete(`/admin/contacts/${contactId}`);
export const adminContactsCleanupAllOrphans = () => api.post('/admin/contacts/cleanup-all-orphans');

// ─── Alarmas ───────────────────────────────────────────────
export const adminAlarmsHealth = () => api.get('/admin/alarms/health');
export const adminAlarmsErrors = () => api.get('/admin/alarms/errors');

// ─── Cobros ────────────────────────────────────────────────
export const adminRevenueSummary = () =>
    api.get('/admin/revenue/summary', { headers: areaHeaders('accounting') });
export const adminRevenueSubscriptions = (params = {}) =>
    api.get('/admin/revenue/subscriptions', { params, headers: areaHeaders('accounting') });
export const adminRevenuePayments = (params = {}) =>
    api.get('/admin/revenue/payments', { params, headers: areaHeaders('accounting') });

// ─── Gastos ────────────────────────────────────────────────
export const adminExpensesList = (params = {}) =>
    api.get('/admin/expenses', { params, headers: areaHeaders('accounting') });
export const adminExpensesCreate = (data) =>
    api.post('/admin/expenses', data, { headers: areaHeaders('accounting') });
export const adminExpensesUpdate = (id, data) =>
    api.put(`/admin/expenses/${id}`, data, { headers: areaHeaders('accounting') });
export const adminExpensesRemove = (id) =>
    api.delete(`/admin/expenses/${id}`, { headers: areaHeaders('accounting') });
export const adminExpensesLiveUsage = (force = false) =>
    api.get('/admin/expenses/live-usage', {
        params: force ? { force: 1 } : {},
        headers: areaHeaders('accounting')
    });

// ─── Facturas ──────────────────────────────────────────────
export const adminInvoicesList = (params = {}) =>
    api.get('/admin/invoices', { params, headers: areaHeaders('accounting') });
export const adminInvoiceGet = (id) =>
    api.get(`/admin/invoices/${id}`, { headers: areaHeaders('accounting') });
export const adminInvoiceCreate = (data) =>
    api.post('/admin/invoices', data, { headers: areaHeaders('accounting') });
export const adminInvoiceUpdate = (id, data) =>
    api.put(`/admin/invoices/${id}`, data, { headers: areaHeaders('accounting') });
export const adminInvoiceMarkPaid = (id) =>
    api.post(`/admin/invoices/${id}/mark-paid`, {}, { headers: areaHeaders('accounting') });
export const adminInvoiceVoid = (id) =>
    api.post(`/admin/invoices/${id}/void`, {}, { headers: areaHeaders('accounting') });
